import CallbackRegistryFactory, { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42 } from "../../glue";
import { Glue42Core } from "@glue42/core";

const ThemesConfigurationMethodName = "T42.Themes.Configuration";
interface ThemesConfiguration {
    setThemesMethodName: string;
    getThemesMethodName: string;
    sharedContextName: string;
    getThemesConfigurationMethodName: string;
}
interface GetResult {
    all: Glue42.Themes.Theme[];
    selected: string;
}
export class ThemesImpl implements Glue42.Themes.API {
    private registry: CallbackRegistry = CallbackRegistryFactory();
    private sharedContextName: string;
    private getMethodName: string;
    private setMethodName: string;
    private isSubscribed = false;

    constructor(private core: Glue42Core.GlueCore) {
        this.getConfiguration();
    }

    public async list(): Promise<Glue42.Themes.Theme[]> {
        await this.getConfiguration();
        if (!this.getMethodName) {
            throw new Error("not supported");
        }
        return (await this.getAll()).returned.all;
    }

    public async getCurrent(): Promise<Glue42.Themes.Theme> {
        await this.getConfiguration();
        if (!this.getMethodName) {
            throw new Error("not supported");
        }
        const all = await this.getAll();
        return all.returned.all.find((t) => t.name === all.returned.selected);
    }

    public async select(theme: string): Promise<void> {
        await this.getConfiguration();
        if (!this.setMethodName) {
            throw new Error("not supported");
        }

        await this.core.interop.invoke(this.setMethodName, { theme });
    }

    public onChanged(callback: (theme: Glue42.Themes.Theme) => any): UnsubscribeFunction {
        this.subscribe();
        return this.registry.add("changed", callback);
    }

    private async getConfiguration() {
        try {
            if (this.sharedContextName) {
                return;
            }
            const config = await this.core.interop.invoke<ThemesConfiguration>(ThemesConfigurationMethodName);
            this.sharedContextName = config.returned.sharedContextName;
            this.getMethodName = config.returned.getThemesMethodName;
            this.setMethodName = config.returned.setThemesMethodName;
        } catch (error) {
            return;
        }
    }

    private async getAll() {
        await this.getConfiguration();
        return await this.core.interop.invoke<GetResult>(this.getMethodName);
    }

    private async subscribe() {
        await this.getConfiguration();
        if (this.isSubscribed) {
            return;
        }
        this.isSubscribed = true;
        this.core.contexts.subscribe(this.sharedContextName, (data: { all: Glue42.Themes.Theme[], selected: string }) => {
            if (data && data.all && data.selected) {
                this.registry.execute("changed", data.all.find((t) => t.name === data.selected));
            }
        });
    }
}
